/* deleteSiteUtility.js */

"use strict";

const SITE_NAME_MAX_LENGTH = 60;
const SITE_NAME_LIST_LENGTH = 10;

const ROOT_SITE_MESSAGE = "Sorry, you can't delete the root site so it won't be removed.\n\n";
const CHILD_SITE_MESSAGE = "Deleting a site also deletes its child sites. ";

/**
 * Builds the "site has devices" rejection copy, matching plurality to the number of offending sites.
 * @param {Number} deviceSiteCount Number of selected sites that still have devices assigned.
 * @returns {String} The error message to display.
 */
function buildDeviceSiteMessage(deviceSiteCount) {
    return (
        "Sorry, you can’t delete a site that has devices assigned to it.\n\nRemove the devices from the site" +
        (deviceSiteCount > 1 ? "s" : "") +
        " and " +
        (deviceSiteCount > 1 ? "their" : "its") +
        " child sites."
    );
}

/**
 * Truncates a site name for display in a confirmation message.
 * @param {String} siteName The full site name.
 * @returns {String} The name, ellipsized if it exceeds SITE_NAME_MAX_LENGTH.
 */
function shortenSiteName(siteName) {
    let name = siteName || "";
    return name.length > SITE_NAME_MAX_LENGTH
        ? name.substring(0, SITE_NAME_MAX_LENGTH - 1) + "…"
        : name;
}

/**
 * Expands a list of site ids to include every descendant site. The delete work expands descendants
 * server-side but only reports the ids it was handed, so the caller must expand up front for the
 * confirmation count and the progress veil to reflect what is actually being deleted.
 * @param {Object} SiteEntity The Site entity definition.
 * @param {String} orgId The active organization id.
 * @param {Array} siteIds The selected site ids.
 * @returns {Promise} Resolves with the de-duplicated list of selected ids plus all descendants.
 */
function expandSiteIds(SiteEntity, orgId, siteIds) {
    let expandedIds = [...siteIds];
    if (!expandedIds.length) {
        return Promise.resolve(expandedIds);
    }
    return SiteEntity.loadSites(orgId, {ascendantIds: {$in: siteIds}}).then((childSites) => {
        (childSites || []).forEach((childSite) => {
            if (!expandedIds.includes(childSite.id)) {
                expandedIds.push(childSite.id);
            }
        });
        return expandedIds;
    });
}

/**
 * Determines whether the expanded selection can be deleted, and whether the confirmation should
 * warn about child sites.
 * @param {Object} SiteEntity The Site entity definition.
 * @param {String} orgId The active organization id.
 * @param {Array} expandedIds The expanded site id list from expandSiteIds().
 * @returns {Promise} Resolves with {hasDeviceSite, hasDeviceSiteCount, hasChildSite}.
 */
function checkDeleteBlockers(SiteEntity, orgId, expandedIds) {
    const blockers = {hasDeviceSite: false, hasDeviceSiteCount: 0, hasChildSite: false};
    if (!expandedIds.length) {
        return Promise.resolve(blockers);
    }
    return SiteEntity.loadSites(orgId, {
        $and: [{id: {$in: expandedIds}}, {deviceCount: {$gt: 0}}],
    })
        .then((sites) => {
            if (sites && sites.length > 0) {
                blockers.hasDeviceSite = true;
                blockers.hasDeviceSiteCount = sites.length;
                return Promise.resolve();
            }
            return SiteEntity.loadSites(orgId, {
                $and: [{id: {$in: expandedIds}}, {childSiteCount: {$gt: 0}}],
            }).then((childSiteParents) => {
                if (childSiteParents && childSiteParents.length > 0) {
                    blockers.hasChildSite = true;
                }
            });
        })
        .then(() => blockers);
}

/**
 * Assembles the delete confirmation message. Small selections are enumerated by name; larger ones
 * are summarized with the expanded site count.
 * @param {Object} options expandedIds, siteNames, hasChildSite and isRootSite.
 * @returns {String} The confirmation message.
 */
function buildDeleteMessage({expandedIds, siteNames, hasChildSite, isRootSite}) {
    let names = siteNames ? siteNames.map((name) => name || "") : undefined;
    let s = names && names.length === 1 ? "" : "s";
    let message = "";

    if (isRootSite) {
        message = ROOT_SITE_MESSAGE;
    }

    if (hasChildSite) {
        message += CHILD_SITE_MESSAGE;
    }

    let listEnumerable = expandedIds.length <= SITE_NAME_LIST_LENGTH;
    message += `Are you sure you want to delete the ${
        listEnumerable ? "" : expandedIds.length + " "
    }site${s}?\n`;

    if (listEnumerable && names) {
        names.forEach((siteName) => {
            message += "\n" + shortenSiteName(siteName);
        });
    }

    return message;
}

/**
 * Assembles the delete confirmation message for the single-site entry points (site tree, site
 * editor), where the acting site is named and its descendants are summarized as a count.
 * @param {Object} options siteName, expandedIds and hasChildSite.
 * @returns {String} The confirmation message.
 */
function buildSingleSiteDeleteMessage({siteName, expandedIds, hasChildSite}) {
    let childCount = Math.max(0, expandedIds.length - 1);
    let message = hasChildSite || childCount ? CHILD_SITE_MESSAGE : "";

    message += `Are you sure you want to delete the site ${shortenSiteName(siteName)}`;
    message += childCount
        ? ` and its ${childCount} child site${childCount > 1 ? "s" : ""}?\n`
        : "?\n";

    return message;
}

/**
 * Builds the work target for a site delete.
 * @param {Array} expandedIds The expanded site id list from expandSiteIds().
 * @returns {Object} The work target passed to runSiteDeleteWork().
 */
function buildWorkTarget(expandedIds) {
    return {
        method: "multi",
        ids: expandedIds,
        category: "entity",
        entityType: "Site",
    };
}

/**
 * Builds the progress callback that drives the loading veil counter.
 * @param {Object} modalUserFeedback The user feedback component.
 * @returns {Function} A progress callback for runSiteDeleteWork().
 */
function makeProgressCallback(modalUserFeedback) {
    return (prog) => {
        modalUserFeedback.showLoadingProgress(`${prog.successCount} of ${prog.targetCount}`);
    };
}

module.exports = {
    SITE_NAME_LIST_LENGTH,
    ROOT_SITE_MESSAGE,
    buildDeviceSiteMessage,
    expandSiteIds,
    checkDeleteBlockers,
    buildDeleteMessage,
    buildSingleSiteDeleteMessage,
    buildWorkTarget,
    makeProgressCallback,
};
